import i18n from "@emoji-mart/data/i18n/ja.json";
import Picker from "@emoji-mart/react";
import classnames from "classnames";
import split from "graphemesplit";
import takeRight from "lodash/takeRight";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";

import {
  useChatCreateUserMutation,
  useChatDeleteMessageMutation,
  useChatEventGAPush,
  useChatGetAgreementQuery,
  useChatGetRoomsLazyQuery,
  useChatGetRoomTokenLazyQuery,
  useChatGetUserTokenLazyQuery,
  useChatPostMessageMutation,
  useChatSetAgreementMutation,
  useModal,
  useNotification,
} from "@/hooks";
import config from "@/lib/config";
import dayjs from "@/lib/day";
import Styles from "@/styles/Chat.module.scss";

import EmojiIcon from "../../svgs/chat_emoji_icon.svg";
import ChatNewMessageIcon from "../../svgs/chat_new_message.svg";
import ChatPostIcon from "../../svgs/chat_post.svg";
import { Button } from "../Buttons";
import ChatMessageDeleteModal from "./ChatMessageDeleteModal";
import ChatMessageReportModal from "./ChatMessageReportModal";
import GuidelineAgreementModal from "./GuidelineAgreementModal";
import NickNameInputModal from "./NickNameInputModal";

i18n.search_no_results_1 = "該当なし";

const watchPartyOptions = {
  endpoint: config.chat.endpoint,
  types: ["message", "messageUpdated", "error"],
};

const chatEndTimeBefore = 15;
const checkChatEndTime = 30000;
const chatMessageLimit = 200;

const ChatDisplay = ({
  rooms,
  chatDisplay,
  setChatDisplay,
  setChatEnable,
  metaId,
  encodeEndDate,
  user,
}) => {
  const { showModal, hideModal } = useModal();
  const [guidelineAgreement, setGuidelineAgreement] = useState(false);
  const [nickName, setNickName] = useState("");
  const [chatMessageInput, setChatMessageInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const chatRoomInfoDefault = {
    id: null,
    token: null,
    liveEndAfter: null,
    chatEndDateTime: null,
  };
  const [chatRoomInfo, setChatRoomInfo] = useState(chatRoomInfoDefault);
  const chatUserInfoDefault = {
    id: null,
    refId: null,
    roomId: null,
    token: null,
  };
  const [chatUserInfo, setChatUserInfo] = useState(chatUserInfoDefault);
  const [loaded, setLoaded] = useState(false);
  const [isViewingPastMessage, setIsViewingPastMessage] = useState(false);
  const [isMessageControlling, setIsMessageControlling] = useState(false);
  const [mutedMessages, setMutedMessages] = useState([]);
  const [mutedUsers, setMutedUsers] = useState([]);
  const [displayChatEndNotification, setDisplayChatEndNotification] =
    useState("none");
  const [displayEmojiPicker, setDisplayEmojiPicker] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [isChatError, setIsChatError] = useState(false);
  const [isPostedNGMessage, setIsPostedNGMessage] = useState(false);
  const [chatRoomIndex, setChatRoomIndex] = useState(0);
  const [reportedMessageIds, setReportedMessageIds] = useState([]);
  const appMessageLimit =
    rooms?.[chatRoomIndex]?.customData?.appMessageLimit ?? 100;
  const [chatMessagePushStack, setChatMessagePushStack] = useState([]);
  const [chatMessagePopStack, setChatMessagePopStack] = useState([]);
  const chatIntervalMax = 500;
  const chatIntervalMin = 4;
  const chatIntervalIdol =
    rooms?.[chatRoomIndex]?.config?.pollingInterval ?? 5000;
  const [chatInterval, setChatInterval] = useState(chatIntervalIdol);
  const [chatIntervalStandard, setChatIntervalStandard] =
    useState(chatIntervalIdol);

  const watchPartyRef = useRef(null);
  const chatMessageAreaRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatRoomInfoRef = useRef(chatRoomInfo);

  useEffect(() => {
    chatRoomInfoRef.current = chatRoomInfo;
  }, [chatRoomInfo]);

  const intervalId = useRef(null);
  const messageSending = useRef(false);
  const messagesUpdating = useRef(false);
  const messageUpdatingTimeout = useRef(null);

  const [isWarningVisible, setIsWarningVisible] = useState(true);

  const [chatJoined, setChatJoined] = useState(false);
  const roomUpdateIntervalId = useRef(null);
  const roomUpdateIntervalMinute = 5 * 60 * 1000;

  const effectiveChatEndTime = useMemo(() => {
    const fromRoom = chatRoomInfo?.chatEndDateTime
      ? dayjs(chatRoomInfo.chatEndDateTime)
      : null;

    if (fromRoom?.isValid()) {
      return fromRoom;
    }

    return dayjs(encodeEndDate).add(chatRoomInfo?.liveEndAfter || 0, "minute");
  }, [
    encodeEndDate,
    chatRoomInfo?.chatEndDateTime,
    chatRoomInfo?.liveEndAfter,
  ]);

  const count = useMemo(() => {
    return split(chatMessageInput?.trim()).length;
  }, [chatMessageInput]);
  const isChatSendOK = useMemo(() => {
    return (
      count > 0 &&
      count <= chatMessageLimit &&
      chatMessageInput?.trim()?.length !== 0
    );
  }, [count, chatMessageInput]);
  const isChatInputOver = useMemo(() => {
    return count > chatMessageLimit;
  }, [count]);

  const { guideLineAgreementEvent, sendCommentEvent, reportCommentEvent } =
    useChatEventGAPush();

  const hideChat = useCallback(() => {
    setChatDisplay(false);
  }, [setChatDisplay]);
  const turnOnIsComposing = useCallback(() => {
    setIsComposing(true);
  }, []);
  const turnOffIsComposing = useCallback(() => {
    setIsComposing(false);
  }, []);

  // チャット送信制御自体はmessageSendingで行うが、input要素の無効/有効切替後にフォーカスかけたいのでレンダリング用のstateを設ける
  const [isMessageInputOK, setIsMessageInputOK] = useState(true);
  //主に未ログインなどでGraphQLがエラーのときチャットコンポーネント破棄する。課題：一瞬見えることがある
  const agreementOnError = useCallback(() => {
    hideChat();
    setChatEnable(false);
  }, [hideChat, setChatEnable]);
  const { loading: agreementDataLoading } = useChatGetAgreementQuery({
    onCompleted: ({ getChatGuideLineAgreement }) => {
      setGuidelineAgreement(getChatGuideLineAgreement?.agreement);
    },
    onError: agreementOnError,
  });
  const [setAgreementMutation] = useChatSetAgreementMutation({
    onCompleted: ({ setChatGuideLineAgreement }) => {
      setGuidelineAgreement(setChatGuideLineAgreement?.agreement);
      guideLineAgreementEvent(rooms?.[chatRoomIndex]?.roomId);
    },
  });
  const [getRoomTokenQuery] = useChatGetRoomTokenLazyQuery({
    variables: {
      roomId: rooms?.[chatRoomIndex]?.roomId,
    },
  });
  const handleRoomTokenCompleted = (getRoomToken) => {
    if (getRoomToken === null) {
      setIsChatError(true);
    } else {
      setChatRoomInfo({
        id: rooms?.[chatRoomIndex].roomId,
        token: getRoomToken.token,
        liveEndAfter: rooms?.[chatRoomIndex]?.customData?.liveEndAfter,
        chatEndDateTime: rooms?.[chatRoomIndex]?.customData?.chatEndDateTime,
      });
    }
  };

  const handleRoomTokenError = (e) => {
    console.log("error on getRoomToken:", e);
    setIsChatError(true);
  };

  const [createUserMutation] = useChatCreateUserMutation({
    variables: {
      roomId: rooms?.[chatRoomIndex]?.roomId,
      metaId: Number(metaId),
    },
    onCompleted: ({ createUser }) => {
      if (createUser === null) {
        setIsChatError(true);
      } else {
        setChatUserInfo({
          ...chatUserInfo,
          id: createUser.id,
          refId: createUser.refId,
          roomId: createUser.roomId,
        });
      }
    },
    onError: (e) => {
      console.log("error on CreateUser:", e);
      const errorCode = e?.graphQLErrors?.[0]?.extensions?.error?.code;
      setChatUserInfo(chatUserInfoDefault);
      // errorがユーザー上限だった場合roomIndexを次のものにして取得を試みる
      if (Number(errorCode) === 91009) {
        setChatRoomIndex(chatRoomIndex + 1);
      } else {
        setIsChatError(true);
      }
    },
  });
  const [getUserTokenQuery] = useChatGetUserTokenLazyQuery({
    variables: {
      roomId: chatRoomInfo.id,
      userId: chatUserInfo.id,
    },
  });

  const handleUserTokenCompleted = (getUserToken) => {
    if (getUserToken === null) {
      setIsChatError(true);
    } else {
      setChatUserInfo({ ...chatUserInfo, token: getUserToken.token });
      setLoaded(true);
    }
  };

  const handleUserTokenError = (e) => {
    console.log("error on getUserToken:", e);
    setIsChatError(true);
  };

  const [chatDeleteMessageMutation] = useChatDeleteMessageMutation({
    variables: {
      roomId: chatRoomInfo.id,
    },
  });
  const { showNotification } = useNotification();

  const [chatPostMessageMutation] = useChatPostMessageMutation({
    variables: {
      roomId: chatRoomInfo.id,
      userId: chatUserInfo.id,
      refId: chatUserInfo.refId,
      displayName: nickName,
      token: chatUserInfo.token,
    },
  });

  const [getRoomsLazyQuery, { called: getRoomsCalled, refetch: refetchRooms }] =
    useChatGetRoomsLazyQuery({
      fetchPolicy: "no-cache",
      context: {
        fetchOptions: { cache: "no-store" },
        headers: { "Cache-Control": "no-store" },
      },
    });

  // ガイドライン同意ダイアログに渡すハンドル
  const handleAgreementClick = useCallback(() => {
    // ガイドライン同意cookie設定
    setAgreementMutation({});
    hideModal();
  }, []);

  // ニックネーム入力ダイアログに渡すハンドル
  const handleNickNameDecisionClick = useCallback((data) => {
    if (!data.nickName || data.nickName === "" || data.nickName.trim() === "") {
      return;
    }
    setNickName(data.nickName.trim());
    hideModal();
  }, []);

  // チャットメッセージ入力に渡すハンドル
  const handleChatMessageInputChange = useCallback((e) => {
    setChatMessageInput(e.target.value);
  }, []);

  const handleChatMessageOnKeyDown = useCallback(
    (e) => {
      // keyCodeは非推奨だが、safariのみcompositionEndとkeyDownの発火順が異なるためkey=="Enter"では判別不可。
      // safariは変換確定(229)とその他のEnter(13)でkeyCodeが異なるため判別可能
      if (e.isComposing || isComposing) return;

      if (
        e.keyCode === 13 &&
        !isComposing &&
        !messageSending.current &&
        isChatSendOK
      ) {
        e.preventDefault();
        setIsMessageInputOK(false);
        handlePost();
      }
    },
    [isComposing, chatMessageInput, isChatSendOK]
  );

  // チャットメッセージ表示エリアのスクロール時の処理
  const handleChatMessageAreaScroll = useCallback(
    (e) => {
      if (
        //メッセージ更新中は動作させない（チャット量が多い時にチラつくのを防止）
        !messagesUpdating.current &&
        // ジャンプボタン表示にメッセージ要素の高さ×2くらいのマージン持つ
        e.target.scrollTop + e.target.offsetHeight < e.target.scrollHeight - 32
      ) {
        if (!isViewingPastMessage) {
          setIsViewingPastMessage(true);
          setIsWarningVisible(false);
          watchPartyRef.current.pause();
        }
      } else {
        if (isViewingPastMessage) {
          setIsViewingPastMessage(false);
          setIsWarningVisible(true);
          watchPartyRef.current.start();
        }
      }
    },
    [isViewingPastMessage]
  );

  // チャットメッセージ押下時の処理
  const handleChatMessageClick = useCallback(
    (e) => {
      const messageData = chatMessages.filter((message) => {
        return message.id === e.currentTarget.dataset.id;
      });
      const { id, displayName, postDesc, userId } = messageData[0];
      if (userId === chatUserInfo.id) {
        displayChatMessageDeleteModal(messageData[0]);
      } else {
        displayChatMessageReportModal(messageData[0]);
      }
    },
    [chatMessages, chatUserInfo, reportedMessageIds]
  );

  // メッセージ削除ダイアログに渡すハンドル
  const handleChatMessageDeleteClick = useCallback((id) => {
    chatDeleteMessageMutation({
      variables: {
        messageId: id,
      },
      onError: (e) => {
        console.log("error on deleteMessage:", e);
      },
    });
    hideModal();
    watchPartyRef.current.start();
    addMutedMessage(id);
    setIsMessageControlling(false);
  }, []);

  const addMutedMessage = useCallback((id) => {
    setMutedMessages((mutedMessages) => [...mutedMessages, id]);
    unsetMessage(id);
  }, []);

  const addMutedUser = useCallback((userId) => {
    setMutedUsers((mutedUsers) => [...mutedUsers, userId]);
    unsetUserMessages(userId);
  }, []);

  const reportComplete = useCallback(() => {
    showNotification(
      "ご報告いただきありがとうございます。このチャットがガイドラインに違反している場合、速やかに対処いたします。",
      {
        time: 3000,
      }
    );
    hideModal();
    watchPartyRef.current.start();
    setIsMessageControlling(false);
  }, []);

  // 通報済みメッセージIDリスト
  const handleReportComplete = useCallback((messageId) => {
    setReportedMessageIds((prevIds) => [...prevIds, messageId]);
    reportCommentEvent();
  }, []);

  // ガイドライン同意しない場合チャットコンポーネントも閉じる
  const modalAndComponentCloseHandle = useCallback(() => {
    hideModal();
    hideChat();
  }, [hideModal, hideChat]);

  // ガイドライン同意ダイアログ表示
  const displayAgreementModal = useCallback(() => {
    showModal(
      <GuidelineAgreementModal
        handleClick={handleAgreementClick}
        modalAndComponentCloseHandle={modalAndComponentCloseHandle} // モーダルコンポーネントの閉じるボタンを利用しないので、別途CLOSEボタン用の関数を渡す
      />,
      {
        closeButton: false,
        outerAreaClose: false,
        style: "guidelineAgreementModal",
        onClose: hideChat,
      }
    );
  }, [showModal, handleAgreementClick, modalAndComponentCloseHandle, hideChat]);

  // ニックネーム入力ダイアログ表示
  const displayNickNameInputModal = useCallback(() => {
    if (!nickName) {
      showModal(
        <NickNameInputModal
          handleClick={handleNickNameDecisionClick}
          modalCloseHandle={hideModal} // モーダルコンポーネントの閉じるボタンを利用しないので、別途CLOSEボタン用の関数を渡す
        />,
        {
          closeButton: false,
          outerAreaClose: false,
          style: "nickNameInputModal",
        }
      );
    }
  }, [showModal, hideModal, handleNickNameDecisionClick, nickName]);

  // 最新までスクロール処理
  const scrollToRecent = useCallback(() => {
    if (chatMessageAreaRef?.current?.scrollHeight) {
      chatMessageAreaRef.current.scrollTo({
        top: chatMessageAreaRef.current?.scrollHeight,
      });
    }
  }, [chatMessageAreaRef]);

  // ジャンプボタンの処理
  const jumpButtonClickHandler = useCallback(() => {
    //慣性スクロールを止める
    chatMessageAreaRef.current.style.overflowY = "hidden";
    scrollToRecent(); // scrollに戻す前にスクロールさせる必要がある
    chatMessageAreaRef.current.style.overflowY = "scroll";
    //hiddenからscrollに戻した際、スクロールバーの有無で改行が発生してずれている分をジャンプするためもう一度実行する
    scrollToRecent();
    setIsViewingPastMessage(false);
    setIsWarningVisible(true);
    watchPartyRef?.current?.start();
  }, [scrollToRecent]);

  // チャットコンポーネント呼び出し時にガイドライン同意ダイアログを表示する
  useEffect(() => {
    if (loaded && !guidelineAgreement && chatDisplay) {
      displayAgreementModal();
    }
  }, [loaded, guidelineAgreement, chatDisplay]);

  // メタから取得したroomから入室するroomを選択する
  useEffect(() => {
    if (chatRoomIndex >= rooms?.length) {
      console.log("全てのルームが満室です");
      setIsChatError(true);
    } else {
      const roomId = rooms?.[chatRoomIndex]?.roomId;

      if (roomId) {
        getRoomTokenQuery({
          variables: {
            roomId,
          },
        })
          .then((res) => handleRoomTokenCompleted(res.data.getRoomToken))
          .catch(handleRoomTokenError);
      }
    }
  }, [chatRoomIndex]);

  // ルームトークンが取得できたらユーザー作成を行う
  useEffect(() => {
    if (chatRoomInfo.token && !chatUserInfo.id) {
      createUserMutation();
    }
  }, [chatRoomInfo]);

  // ユーザー作成ができたらユーザートークンを取得する
  useEffect(() => {
    if (chatUserInfo.id && !chatUserInfo.token) {
      getUserTokenQuery({
        variables: {
          roomId: chatRoomInfo.id,
          userId: chatUserInfo.id,
        },
      })
        .then((res) => {
          handleUserTokenCompleted(res.data.getUserToken);
        })
        .catch(handleUserTokenError);
    }
  }, [chatUserInfo]);

  // ユーザートークンを取得できたらWatchPartyクライアントを初期化して、ルームに入室する
  useEffect(() => {
    // 引数
    // roomToken
    // project
    // ユーザーID
    // option

    if (
      chatRoomInfo.token &&
      chatUserInfo.id &&
      chatUserInfo.token &&
      guidelineAgreement
    ) {
      watchPartyRef.current = new WatchPartyClient(
        chatRoomInfo.token,
        config.chat.projectId,
        chatRoomInfo.id,
        watchPartyOptions
      );
      // 作成済みユーザー数取得できる
      // const wpRoomInfo = watchPartyRef.current._getRoomInfo(
      //   config.chat.projectId,
      //   chatRoomInfo.id,
      //   (dummy, info) => {
      //     setChatRoomInfo({
      //       ...chatRoomInfo,
      //       userCount: info.userCount,
      //     });
      //   }
      // );

      watchPartyRef.current.on("message", handleMessage);
      watchPartyRef.current.on("messageUpdated", handleMessageUpdated);
      watchPartyRef.current.on("error", handleMessageError);

      handleJoin();
    }

    return () => {
      if (watchPartyRef.current) {
        watchPartyDispose();
      }
    };
  }, [
    chatRoomInfo.token,
    chatRoomInfo.id,
    chatUserInfo,
    watchPartyOptions,
    guidelineAgreement,
  ]);

  // チャット表示時最新までスクロールする
  useEffect(() => {
    // 新しいメッセージを取得したとき、過去メッセージ表示またはメッセージの削除や通報操作中でなければ最新までスクロールする
    if (!isViewingPastMessage && !isMessageControlling) {
      scrollToRecent();
    }
  }, [chatDisplay, chatMessages, scrollToRecent]);

  useEffect(() => {
    if (isMessageInputOK) {
      chatInputRef?.current?.focus();
    } else {
      chatInputRef?.current?.blur();
    }
  }, [isMessageInputOK]);

  // チャット終了の告知・終了をチェックする処理
  const chatAnnounce = useCallback(() => {
    const now = dayjs();
    const chatEndAnnounceTime = effectiveChatEndTime.subtract(
      chatEndTimeBefore,
      "minute"
    );

    if (
      chatEndAnnounceTime.isBefore(now) &&
      effectiveChatEndTime.isAfter(now)
    ) {
      // チャット終了告知時間より後、かつチャット終了時間より前
      setDisplayChatEndNotification("nearlyEnd");
    } else if (effectiveChatEndTime.isBefore(now)) {
      // 現在時刻が終了時間を過ぎている
      setDisplayChatEndNotification("ended");
    } else {
      // 通常時
      setDisplayChatEndNotification("none");
    }
  }, [effectiveChatEndTime]);
  useEffect(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
    chatAnnounce();
    intervalId.current = setInterval(chatAnnounce, checkChatEndTime);

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [encodeEndDate, chatAnnounce]);

  // 非表示時にポーリング停止する、表示時に再開する
  useEffect(() => {
    if (!watchPartyRef.current) return;
    if (chatDisplay) {
      watchPartyRef.current.start();
    } else {
      watchPartyRef.current.pause();
    }
  }, [chatDisplay]);

  // 退室とクライアントの破棄
  const watchPartyDispose = useCallback(() => {
    watchPartyRef.current.pause();
    watchPartyRef.current.leave();
    watchPartyRef.current.off("message", handleMessage);
    watchPartyRef.current.off("messageUpdated", handleMessageUpdated);
    watchPartyRef.current.off("error", handleMessageError);
    delete watchPartyRef.current;
  }, []);

  // 入室処理
  const handleJoin = useCallback(() => {
    if (!watchPartyRef.current) return;
    watchPartyRef.current.join(
      chatUserInfo.id,
      chatUserInfo.token,
      (error, body) => {
        // roomが404
        console.log("join error:", error);
        console.log("join body:", body);
        if (error && error.body?.statusCode === 403) {
        }
        if (error && error.body?.statusCode === 404) {
          // 裏ではjoinできているので不整合が発生する為消しておく
          // TODO: user削除とdisposeを行う
          // this.doDeleteUser(this.user.id);
          if (watchPartyRef.current) {
            watchPartyDispose();
          }
          setIsChatError(true);
        }
        setChatJoined(true);
        // 表示されていたらstart
        if (chatDisplay) watchPartyRef.current.start();
      }
    );
  }, [chatUserInfo, chatDisplay, watchPartyDispose]);

  // チャットメッセージキュー定義
  const setChatIntervalRandom = useCallback(() => {
    if (chatIntervalStandard === chatIntervalIdol) {
      setChatInterval(chatIntervalStandard);
      return;
    }
    // // 0.2 ~ 1.8のランダムをかける、両端に偏った乱数にする（0.2または1.8に近い値が出やすく、中央の1.0は出にくい）
    let baseRandom = (Math.random() + Math.random()) / 2 + 0.5; // 0.5の頻度が高く、0,1に近づくほど頻度の低い乱数を生成し、0.5足す
    if (baseRandom > 1.0) {
      baseRandom = baseRandom - 1.0; // 1.0超えているものを1引くことで、0.5の頻度が低く、0,1に近づくほど頻度の高い乱数になる
    }
    const newChatInterval = chatIntervalStandard * (baseRandom * 1.6 + 0.2);
    setChatInterval(newChatInterval);
  }, [chatIntervalStandard, chatIntervalIdol]);
  const chatMessageEnQueue = useCallback((value) => {
    setChatMessagePushStack((chatMessagePushStack) => [
      ...chatMessagePushStack,
      value,
    ]);
  }, []);
  const chatMessageDeQueue = useCallback(() => {
    const tempChatMessagePopStack = chatMessagePopStack;
    if (tempChatMessagePopStack.length === 0) {
      const tempChatMessagePushStack = chatMessagePushStack;
      if (tempChatMessagePushStack.length > 0) {
        let newChatInterval;
        if (chatMessages.length === 0) {
          newChatInterval = chatIntervalMin;
        } else {
          newChatInterval = Math.max(
            chatIntervalMin,
            Math.min(
              chatIntervalMax,
              chatIntervalIdol / tempChatMessagePushStack.length
            )
          );
        }
        setChatIntervalStandard(newChatInterval);
        while (tempChatMessagePushStack.length > 0) {
          tempChatMessagePopStack.push(tempChatMessagePushStack.pop());
        }
        setChatMessagePushStack([]);
      } else {
        setChatIntervalStandard(chatIntervalIdol);
      }
    }
    const ret = tempChatMessagePopStack?.pop();
    setChatMessagePopStack(tempChatMessagePopStack);
    return ret;
  }, [
    chatIntervalIdol,
    chatMessagePopStack,
    chatMessagePushStack,
    chatMessages,
  ]);

  const chatMessageFilter = useCallback(
    (target) => {
      // NGメッセージは表示しない
      if (target.isNGMessage) {
        return false;
      }
      // 非表示にしているユーザーのメッセージは表示しない
      const filterMutedUser = mutedUsers.filter((user) => {
        return user === target.sender.id;
      });
      if (filterMutedUser.length > 0) {
        return false;
      }
      // 同じメッセージのイベントを複数受信することがあるので確認
      const filterDuplicate = chatMessages.filter((filtered) => {
        return filtered.id === target.id;
      });
      // 同一メッセージだったら表示しない
      if (filterDuplicate.length > 0) {
        return false;
      }
      // ミュート済みのメッセージを受信している場合表示しない
      const filterMutedMessage = mutedMessages.filter((filtered) => {
        return filtered === target.id;
      });
      // 同一メッセージだったら表示しない
      if (filterMutedMessage.length > 0) {
        return false;
      }
      return true;
    },
    [mutedUsers, mutedMessages, chatMessages]
  );

  // メッセージ受信イベントに対する処理
  const handleMessage = useCallback(
    (event) => {
      if (!chatMessageFilter(event)) return;

      chatMessageEnQueue(event);
      if (chatMessages.length === 0) {
        setChatInterval(chatIntervalMin);
      }
    },
    [chatMessages, chatMessageFilter, chatMessageEnQueue]
  );

  // メッセージ更新イベントの処理
  const handleMessageUpdated = useCallback((event) => {
    // NGメッセージは表示しない
    if (event?.message?.data?.isNGMessage) {
      unsetMessage(event?.message?.data?.id);
    }
  }, []);

  // エラーイベントの処理
  const handleMessageError = useCallback((event) => {
    console.log(event);
  }, []);

  const chatMessageModifier = useCallback((update) => {
    if (messageUpdatingTimeout.current)
      clearTimeout(messageUpdatingTimeout.current);
    messagesUpdating.current = true;
    setChatMessages(update);
    messageUpdatingTimeout.current = setTimeout(() => {
      messagesUpdating.current = false;
    }, 50);
  }, []);

  // 指定のメッセージをフロント上で非表示にする
  const unsetMessage = useCallback(
    (id) => {
      chatMessageModifier((chatMessages) =>
        chatMessages.filter((message) => {
          return message.id !== id;
        })
      );
    },
    [chatMessageModifier]
  );

  // 指定のユーザーが投稿したメッセージをフロント上で非表示にする
  const unsetUserMessages = useCallback(
    (userId) => {
      chatMessageModifier((chatMessages) =>
        chatMessages.filter((message) => {
          return message.userId !== userId;
        })
      );
    },
    [chatMessageModifier]
  );

  // チャット投稿処理
  const handlePost = useCallback(() => {
    if (!watchPartyRef.current) {
      setIsMessageInputOK(true);
      return;
    }
    if (!chatMessageInput || chatMessageInput.trim() === "") {
      setChatMessageInput("");
      setIsMessageInputOK(true);
      return;
    }
    setIsPostedNGMessage(false);
    if (messageSending.current) return;
    messageSending.current = true;
    setIsMessageInputOK(false);

    // LOGICAでの投稿
    const trimmedMessage = chatMessageInput.trim();
    chatPostMessageMutation({
      variables: {
        message: trimmedMessage,
      },
      onCompleted: ({ postChatMessage }) => {
        if (postChatMessage?.isNGMessage) {
          setIsPostedNGMessage(true);
        } else {
          setChatMessageInput("");
          setIsPostedNGMessage(false);
          chatMessageModifier((chatMessages) => {
            return [
              ...takeRight(chatMessages, appMessageLimit - 1),
              {
                id: postChatMessage.id,
                displayName: postChatMessage.sender?.displayName,
                userId: postChatMessage.sender.id,
                postDesc: postChatMessage.message,
              },
            ];
          });
          jumpButtonClickHandler();
          sendCommentEvent(chatRoomInfo.id, user?.hashedUserId);
        }
        messageSending.current = false;
        setIsMessageInputOK(true);
      },
      onError: (e) => {
        messageSending.current = false;
      },
    });
  }, [
    chatMessageInput,
    nickName,
    appMessageLimit,
    jumpButtonClickHandler,
    chatMessageModifier,
    chatRoomInfo,
    user,
  ]);

  // チャットメッセージキューを処理
  const chatIntervalId = useRef(null);
  const chatIntervalProcess = useCallback(() => {
    if (!isViewingPastMessage) {
      const message = chatMessageDeQueue();
      if (message !== undefined && message !== null) {
        if (chatMessageFilter(message)) {
          chatMessageModifier((chatMessages) => {
            return [
              ...takeRight(chatMessages, appMessageLimit - 1),
              {
                id: message.id,
                displayName: message.sender?.displayName,
                userId: message.sender.id,
                postDesc: message.message,
              },
            ];
          });
          setChatIntervalRandom();
        }
      }
    }
  }, [
    appMessageLimit,
    isViewingPastMessage,
    setChatIntervalRandom,
    chatMessageFilter,
    chatMessageDeQueue,
    chatMessageModifier,
  ]);

  useEffect(() => {
    clearTimeout(chatIntervalId.current);
    if (chatDisplay) {
      chatIntervalId.current = setInterval(chatIntervalProcess, chatInterval);
    }
    return () => {
      clearTimeout(chatIntervalId.current);
    };
  }, [chatDisplay, chatInterval, chatIntervalProcess]);
  useEffect(() => {
    setChatIntervalRandom();
  }, [chatIntervalStandard, setChatIntervalRandom]);

  const toggleDisplayEmojipicker = useCallback(() => {
    if (nickName) {
      setDisplayEmojiPicker(!displayEmojiPicker);
    }
  }, [nickName, displayEmojiPicker]);

  const handleEmojiPick = useCallback((e) => {
    //setChatMessageInput(chatMessageInput + e.native);
    let value = chatInputRef.current?.value;
    let pos = chatInputRef.current?.selectionStart;

    value = value.split("");
    value.splice(pos, 0, e.native);
    value = value.join("");
    chatInputRef.current.value = value;
    setChatMessageInput(value);

    chatInputRef.current.setSelectionRange(
      pos + e.native.length,
      pos + e.native.length
    );
    chatInputRef.current.focus();
    setDisplayEmojiPicker(false);
  }, []);

  // メッセージ削除ダイアログ表示
  const modalOnClose = useCallback(() => {
    watchPartyRef.current.start();
    setIsMessageControlling(false);
  }, []);
  const modalCloseHandle = useCallback(() => {
    hideModal();
    modalOnClose();
  }, [hideModal, modalOnClose]);
  const displayChatMessageDeleteModal = useCallback(
    (props) => {
      setIsMessageControlling(true);
      watchPartyRef.current.pause();
      showModal(
        <ChatMessageDeleteModal
          handleClick={handleChatMessageDeleteClick}
          modalCloseHandle={modalCloseHandle} // モーダルコンポーネントの閉じるボタンを利用しないので、別途CLOSEボタン用の関数を渡す
          {...props}
        />,
        {
          closeButton: false,
          outerAreaClose: false,
          style: "chatMessageDeleteModal",
          onClose: modalOnClose,
        }
      );
    },
    [showModal, modalCloseHandle, handleChatMessageDeleteClick, modalOnClose]
  );

  // メッセージ非表示/通報ダイアログ表示
  const displayChatMessageReportModal = useCallback(
    (props) => {
      setIsMessageControlling(true);
      watchPartyRef.current.pause();
      showModal(
        <ChatMessageReportModal
          modalCloseHandle={modalCloseHandle} // モーダルコンポーネントの閉じるボタンを利用しないので、別途CLOSEボタン用の関数を渡す
          roomId={chatRoomInfo.id}
          {...props}
          addMutedMessage={addMutedMessage}
          addMutedUser={addMutedUser}
          handleReportComplete={handleReportComplete}
          reportComplete={reportComplete}
          reportedMessageIds={reportedMessageIds}
        />,
        {
          closeButton: false,
          outerAreaClose: false,
          style: "chatMessageReportModal",
          onClose: modalOnClose,
        }
      );
    },
    [
      showModal,
      chatRoomInfo,
      reportedMessageIds,
      handleReportComplete,
      reportComplete,
      modalCloseHandle,
      modalOnClose,
      addMutedMessage,
      addMutedUser,
    ]
  );

  const windowReload = useCallback(() => {
    window.location.reload();
  }, []);

  // ルーム情報定期更新
  const fetchRoom = useCallback(async () => {
    try {
      // 最新の chatRoomInfo は ref から参照（依存配列に入れて fetchRoom と useEffect で循環しないようにする）
      const currentRoomId = chatRoomInfoRef.current?.id;
      const currentChatEnd = chatRoomInfoRef.current?.chatEndDateTime;

      const roomVariables = {
        metaId: Number(metaId),
      };
      const { data: roomsResult } =
        getRoomsCalled && refetchRooms
          ? await refetchRooms(roomVariables)
          : await getRoomsLazyQuery({ variables: roomVariables });
      // roomsResult.getRooms.rooms の中から現在入室中の room を探す
      const roomsList = roomsResult?.getRooms?.rooms ?? [];
      const matchedRoom = roomsList
        .filter((r) => r !== null)
        .find((r) => r.roomId === currentRoomId);

      if (matchedRoom) {
        // サーバが明示的に空文字/ null を返してクリアするケースにも対応する
        const matchedEndRaw = matchedRoom?.customData?.chatEndDateTime;
        const matchedEnd = matchedEndRaw === "" ? null : matchedEndRaw ?? null;

        const prevHasValid =
          typeof currentChatEnd === "string" &&
          currentChatEnd.length > 0 &&
          dayjs(currentChatEnd).isValid();
        const matchedHasValid =
          typeof matchedEnd === "string" &&
          matchedEnd.length > 0 &&
          dayjs(matchedEnd).isValid();

        let shouldUpdate = false;
        if (!prevHasValid && matchedHasValid) {
          // 以前が無効で取得値が有効 => 更新
          shouldUpdate = true;
        } else if (prevHasValid && !matchedHasValid) {
          // 以前が有効で取得値が無効(null 等) => クリアなので更新
          shouldUpdate = true;
        } else if (prevHasValid && matchedHasValid) {
          // 両方有効なら日付で比較
          shouldUpdate = !dayjs(currentChatEnd).isSame(dayjs(matchedEnd));
        } else {
          // 両方無効なら文字列比較で差分判定（null と "" などを区別する）
          shouldUpdate = String(currentChatEnd) !== String(matchedEnd);
        }

        if (shouldUpdate) {
          // 関数型更新にして、await 中に別のルームに切り替わっていたら上書きしない
          setChatRoomInfo((prev) => {
            if (!prev || prev.id !== currentRoomId) return prev;
            return {
              ...prev,
              chatEndDateTime: matchedEnd === null ? null : String(matchedEnd),
            };
          });
        }
      }
    } catch (e) {
      console.log("fetchRoom error:", e);
    }
  }, [getRoomsLazyQuery, getRoomsCalled, refetchRooms, metaId]);

  useEffect(() => {
    if (watchPartyRef.current && chatJoined && chatDisplay && !isChatError) {
      if (roomUpdateIntervalId.current) {
        clearInterval(roomUpdateIntervalId.current);
      }
      roomUpdateIntervalId.current = setInterval(
        fetchRoom,
        roomUpdateIntervalMinute
      );
    }
    return () => {
      if (roomUpdateIntervalId.current) {
        clearInterval(roomUpdateIntervalId.current);
        roomUpdateIntervalId.current = null;
      }
    };
  }, [
    chatJoined,
    roomUpdateIntervalMinute,
    fetchRoom,
    chatDisplay,
    isChatError,
  ]);

  return (
    <>
      {chatDisplay && !isChatError && !agreementDataLoading && (
        <div className={Styles.container}>
          <div className={Styles.chatHeader}>
            <p>チャット</p>
            <Button chatCloseX onClick={hideChat}>
              ×
            </Button>
          </div>
          <hr style={{ width: "100%", borderColor: "#101010" }} />
          {displayChatEndNotification === "nearlyEnd" && isWarningVisible && (
            <div className={Styles.popupNotification}>
              <p>
                まもなくチャットが終了します。
                <br />
                チャット機能の利用は{effectiveChatEndTime.format("HH:mm")}
                までです。
              </p>
            </div>
          )}
          {displayChatEndNotification === "ended" && (
            <div className={Styles.popupNotification} style={{ top: "50%" }}>
              <p>
                チャット欄を終了しました。
                <br />
                ご利用ありがとうございました！
              </p>
            </div>
          )}
          {displayChatEndNotification !== "ended" && (
            <>
              <div
                className={Styles.chatDisplay}
                onScroll={handleChatMessageAreaScroll}
                ref={chatMessageAreaRef}
              >
                <div className={Styles.chatDisplayAlignBottom}>
                  {/* チャット投稿内容 */}
                  {chatMessages.map((item) => (
                    <div
                      className={Styles.chatMessageDisplay}
                      key={item.id}
                      data-id={item.id}
                      onClick={handleChatMessageClick}
                    >
                      <div className={Styles.chatMessage}>
                        <span className={Styles.username}>
                          {item.displayName}
                        </span>
                        <span className={Styles.messageContent}>
                          {item.postDesc}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={Styles.footer}>
                <div className={Styles.chatScrollToRecentBtnArea}>
                  {isViewingPastMessage && (
                    <Button
                      className={Styles.chatScrollToRecentBtn}
                      onClick={jumpButtonClickHandler}
                    >
                      <ChatNewMessageIcon />
                    </Button>
                  )}
                </div>
                <div className={Styles.chatInputContainer}>
                  {isPostedNGMessage && (
                    <div className={Styles.isPostedNGMessage}>
                      <div className={Styles.isPostedNGMessageContent}>
                        <p>
                          不適切な表現が含まれている可能性があります。
                          <br />
                          見直して改めて投稿してください。
                        </p>
                      </div>
                    </div>
                  )}
                  <div className={Styles.chatInputSubContainer}>
                    <div
                      className={classnames(Styles.chatInputUIContainar, {
                        [Styles.isPostedNGMessage]:
                          isPostedNGMessage || isChatInputOver,
                      })}
                      onClick={displayNickNameInputModal}
                    >
                      <input
                        className={Styles.chatInput}
                        ref={chatInputRef}
                        placeholder="チャットを入力..."
                        value={chatMessageInput}
                        onChange={handleChatMessageInputChange}
                        onKeyDown={handleChatMessageOnKeyDown}
                        onCompositionStart={turnOnIsComposing}
                        onCompositionEnd={turnOffIsComposing}
                        disabled={messageSending?.current && isMessageInputOK}
                      />
                      <button
                        onClick={toggleDisplayEmojipicker}
                        disabled={messageSending?.current}
                      >
                        <EmojiIcon
                          className={classnames("", {
                            [Styles.displayEmojiPicker]: displayEmojiPicker,
                          })}
                        />
                      </button>
                      {displayEmojiPicker && (
                        <Picker
                          onEmojiSelect={handleEmojiPick}
                          i18n={i18n}
                          theme={"dark"}
                          searchPosition={"none"}
                          previewPosition={"none"}
                          skinTonePosition={"none"}
                          navPosition={"none"}
                          dynamicWidth={true}
                          maxFrequentRows={1}
                          emojiSize={16}
                          native={true}
                        />
                      )}
                    </div>
                    <div className={Styles.chatInputSendContainar}>
                      <div className={Styles.inputCount}>
                        {chatMessageInput.length > 0 && !isChatSendOK && (
                          <p key="chatInputCount" className={Styles.countAlert}>
                            {chatMessageLimit - count}
                          </p>
                        )}
                        {isChatSendOK && (
                          <p key="chatInputCount" className={Styles.countOk}>
                            {chatMessageLimit - count}
                          </p>
                        )}
                      </div>
                      <Button
                        fillMainColor
                        borderStyle
                        chatSend
                        onClick={
                          nickName ? handlePost : displayNickNameInputModal
                        }
                        disabled={!isChatSendOK || messageSending?.current}
                      >
                        <ChatPostIcon />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {chatDisplay && isChatError && (
        <div className={Styles.container}>
          <div className={Styles.chatHeader}>
            <p>チャット</p>
            <Button chatCloseX onClick={hideChat}>
              ×
            </Button>
          </div>
          <hr style={{ width: "100%", borderColor: "#101010" }} />
          <div className={Styles.chatDisplay}>
            <div className={Styles.chatMessage}>
              <span className={Styles.messageContent}>
                エラーが発生しました。
              </span>
              <Button fillColor medium onClick={windowReload}>
                再読み込みする
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatDisplay;
